// ink-component
import React, { ChangeEvent, Component } from 'react';

import Control from '../Control/Control';
import Icon from '../Icon/Icon';
import Option, { OptionType } from './Option';

import { bemHelper } from '../../helpers';
import setFocus from '../../helpers/dom/setFocus';

import getGlobalProps from '../../helpers/constants/global-props';

type MetaType = {
  initial: string;
  invalid: boolean;
};

type InputType = {
  value?: string | number;
  onFocus?: (event: ChangeEvent<HTMLSelectElement>) => void;
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (event: ChangeEvent<HTMLSelectElement>) => void;
};

export type GroupLabelOptionType = {
  groupLabel: string;
  options: OptionType[];
};

export type SelectOptionType = GroupLabelOptionType | OptionType;

export interface SelectProps {
  /** Unique Identifier */
  id: string;
  /** Options to populate the select.
   * Can be both a simple array of options,
   * or and array of option groups with options inside. */
  options: SelectOptionType[];
  /** Unique name */
  name?: string;
  /** External control for the select */
  value?: string | number;
  /** onFocus Callback. Passes the Event */
  onFocus?: (event: ChangeEvent<HTMLSelectElement>) => void;
  /** onChange Callback. Passes the Event */
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  /** onBlur Callback. Passes the Event */
  onBlur?: (event: ChangeEvent<HTMLSelectElement>) => void;
  /** Select placeholder */
  placeholder?: string;
  /** Select size */
  size?: 'full' | 'large' | 'half' | 'quarter';
  /** Modifier for table */
  isCell?: boolean;
  /** Modifier for disabled state */
  isDisabled?: boolean;
  /** Modifier for inline */
  isInline?: boolean;
  /** Modifier for not valid state */
  isInvalid?: any;
  /** Modifier for selectable placeholders */
  selectablePlaceholder?: boolean;
  /** HTML type */
  autoFocus?: boolean;
  /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
  'aria-label'?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;

  /** @ignore */
  meta?: MetaType;
  /** @ignore */
  input?: InputType;
  /** @ignore */
  defaultValue?: string;
  /** @ignore */
  dataPrivate?: string | boolean;
}

interface State {
  value?: string | number;
}

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @purpose Select one answer among three or more predefined options
 * @categories form
 * @deprecated This component is deprecated and replaced by [NewSelect](https://ink.carta.com/components/NewSelect/)
 * @status deprecated
 * @replacementComponent NewSelect
 * @dateOfDeletion December 1st, 2023
 */
class Select extends Component<SelectProps, State> {
  selectRef: React.RefObject<any>;

  constructor(props: SelectProps) {
    super(props);

    this.selectRef = React.createRef();

    this.state = {
      value: props.input?.value || props.value || props.meta?.initial || props.defaultValue,
    };
    this.handleFocus = this.handleFocus.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
  }

  componentDidMount() {
    if (this.props.autoFocus) setFocus(this.selectRef);
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props?.input?.value !== nextProps.input.value) {
      this.setState({
        value: nextProps.input.value,
      });
    } else if (this.props.value !== nextProps.value) {
      this.setState({
        value: nextProps.value,
      });
    }
  }

  defineClassName() {
    const b = bemHelper();

    return b('ink-control--select', {
      cell: this.props.isCell,
    });
  }

  handleFocus(e: ChangeEvent<HTMLSelectElement>) {
    if (this.props?.input?.onFocus) {
      this.props?.input?.onFocus(e);
    }
    if (this.props?.onFocus) {
      this.props?.onFocus(e);
    }
  }

  handleChange(e: ChangeEvent<HTMLSelectElement>) {
    if (this.props?.input?.onChange) {
      this.props?.input?.onChange(e);
    }
    if (this.props?.onChange) {
      this.props?.onChange(e);
    }

    this.setState({
      value: e.target.value,
    });
  }

  handleBlur(e: ChangeEvent<HTMLSelectElement>) {
    if (this.props?.input?.onBlur) {
      this.props?.input?.onBlur(e);
    }
    if (this.props?.onBlur) {
      this.props?.onBlur(e);
    }
  }

  renderOptions(opts: OptionType[]) {
    return opts.map((opt, idx) => (
      <Option key={idx} label={opt.label} value={opt.value} selectedValue={this.state.value} />
    ));
  }

  renderGroupLabelOptions() {
    return this.props.options.map((option: any, index) => {
      if (option.groupLabel) {
        return (
          <optgroup key={index} label={option.groupLabel}>
            {this.renderOptions(option.options)}
          </optgroup>
        );
      }
      return <Option key={index} label={option.label} value={option.value} selectedValue={this.state.value} />;
    });
  }

  render() {
    const {
      id,
      name,
      size,
      isCell,
      isInline,
      isInvalid,
      isDisabled,
      placeholder,
      selectablePlaceholder,
      autoFocus,
      dataPrivate,
    } = this.props;
    return (
      <Control
        type="select"
        inputId={id}
        size={size}
        isCell={isCell}
        isInline={isInline}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        dataPrivate={dataPrivate}
        data-testid={this.props['data-testid']}
      >
        <select
          id={id}
          ref={this.selectRef}
          aria-label={this.props['aria-label']}
          name={name}
          className={this.defineClassName()}
          onChange={this.handleChange}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          value={this.state.value}
          disabled={isDisabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          {...globalProps}
        >
          <option
            value=""
            disabled={!selectablePlaceholder}
            aria-selected={!selectablePlaceholder && this.state.value === ''}
          >
            {placeholder}
          </option>
          {this.renderGroupLabelOptions()}
        </select>
        <div className="ink-control--select-arrow">
          <Icon type="down" meaning="open select" size="lg" />
        </div>
      </Control>
    );
  }

  static defaultProps = {
    name: undefined,
    value: '',
    onFocus: undefined,
    onChange: undefined,
    onBlur: undefined,
    size: 'large',
    placeholder: '',
    isCell: false,
    isDisabled: false,
    isInline: false,
    isInvalid: false,
    selectablePlaceholder: false,
    autoFocus: false,

    dataPrivate: undefined,
    'data-testid': undefined,
    defaultValue: '',
    meta: {},
    input: {},
  };

  static displayName = 'Select';
}

export default Select;
